import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2, Search, BookOpen, Calendar, Clock, ArrowRight, List, Grid } from 'lucide-react';
import gsap from 'gsap';
import { fetchBlogsThunk, setCurrentBlog } from '../redux/slice/blog.slice';
import { Helmet } from 'react-helmet-async';
import { restoreScrollPosition } from '../utils/scrollRestoration';
import ProgressiveImage from '../components/common/ProgressiveImage';
import useGridCorners from '../hooks/useGridCorners';

function BlogCardSkeleton({ viewMode, style = {} }) {
    if (viewMode === 'vertical') {
        return (
            <div
                style={style}
                className="bg-[#080814]/95 cyber-card-glow overflow-hidden shadow-xl flex flex-col justify-between min-w-0 animate-pulse animate-shimmer h-full"
            >
                <div>
                    {/* Image Banner Skeleton */}
                    <div className="w-full aspect-[16/10] bg-white/5 relative shrink-0" style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
                        {/* Top-Left Category Badge Skeleton */}
                        <div className="absolute top-2.5 left-2.5 h-5 w-20 bg-white/10 rounded-lg border border-white/5"></div>
                        {/* Read Time Overlay Skeleton */}
                        <div className="absolute bottom-2.5 right-2.5 h-4 w-14 bg-white/10 rounded-md border border-white/5"></div>
                    </div>
                    {/* Card Content Skeleton */}
                    <div className="p-4 sm:p-5 space-y-2.5">
                        {/* Meta Date */}
                        <div className="h-3.5 w-24 bg-white/10 rounded"></div>
                        {/* Title (1 Line) */}
                        <div className="h-5 w-5/6 bg-white/15 rounded-md"></div>
                        {/* Excerpt (1 Line) */}
                        <div className="h-3.5 w-full bg-white/10 rounded"></div>
                    </div>
                </div>
                {/* Action Footer Skeleton */}
                <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-3 border-t border-white/5 flex items-center justify-between">
                    <div className="h-3.5 w-24 bg-white/10 rounded"></div>
                </div>
            </div>
        );
    }

    return (
        <div
            style={style}
            className="bg-[#080814]/95 cyber-card-glow overflow-hidden shadow-xl flex flex-col md600:flex-row items-stretch min-w-0 animate-pulse animate-shimmer h-full"
        >
            {/* Horizontal Left Banner Skeleton */}
            <div className="w-full md600:w-48 lg:w-56 aspect-[16/10] bg-white/5 relative shrink-0">
                <div className="absolute top-2.5 left-2.5 h-5 w-20 bg-white/10 rounded-lg border border-white/5"></div>
                <div className="absolute bottom-2.5 right-2.5 md600:hidden h-4 w-14 bg-white/10 rounded-md border border-white/5"></div>
            </div>
            {/* Horizontal Right Details Skeleton */}
            <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-2.5 min-w-0">
                <div className="space-y-2 min-w-0">
                    <div className="flex items-center justify-between">
                        <div className="h-3.5 w-24 bg-white/10 rounded"></div>
                        <div className="hidden md600:block h-3.5 w-16 bg-white/10 rounded"></div>
                    </div>
                    <div className="h-5 w-4/5 bg-white/15 rounded-md"></div>
                    <div className="h-3.5 w-full bg-white/10 rounded"></div>
                </div>
                <div className="pt-2.5 border-t border-white/5 flex items-center justify-between">
                    <div className="h-3.5 w-24 bg-white/10 rounded"></div>
                </div>
            </div>
        </div>
    );
}

export default function Blog() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const containerRef = useRef(null);
    const headerRef = useRef(null);

    const { blogs, pagination, loading } = useSelector((state) => state.blog);

    // Filters & View Mode
    const [searchInput, setSearchInput] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('vertical'); // 'vertical' | 'horizontal'
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [isInitialFetched, setIsInitialFetched] = useState(() => Boolean(blogs && blogs.length > 0));

    const { gridCols, setGridRef, getCornerStyle } = useGridCorners({ viewMode });

    // Track previous search and initial mount
    const isFirstMount = useRef(true);
    const lastSearchRef = useRef(null);

    // Debounce search typing
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            setSearchQuery(searchInput);
        }, 400);
        return () => clearTimeout(delayDebounceFn);
    }, [searchInput]);

    // Fetch blogs when search changes or initial fetch
    useEffect(() => {
        const cleanSearch = searchQuery.trim();

        // If on initial mount we already have blogs loaded in Redux and the search hasn't changed:
        if (isFirstMount.current) {
            isFirstMount.current = false;
            lastSearchRef.current = cleanSearch;

            if (blogs && blogs.length > 0) {
                setIsInitialFetched(true);
                const hasSavedScroll = Boolean(sessionStorage.getItem('cg_scroll_/blog'));
                if (hasSavedScroll) {
                    restoreScrollPosition('/blog');
                } else {
                    window.scrollTo(0, 0);
                    if (window.__lenis) {
                        window.__lenis.scrollTo(0, { immediate: true, force: true });
                    }
                }
                return;
            }
        }

        // If search changed or not initial fetched: reset to page 1 and fetch fresh
        if (lastSearchRef.current !== cleanSearch || !isInitialFetched) {
            lastSearchRef.current = cleanSearch;
            sessionStorage.removeItem('cg_scroll_/blog');
            dispatch(fetchBlogsThunk({
                status: 'Published',
                page: 1,
                limit: 12,
                search: cleanSearch || undefined
            })).finally(() => {
                setIsInitialFetched(true);
            });
        }
    }, [dispatch, searchQuery, isInitialFetched]);

    // Instant exact scroll restoration or fresh mount reveal animation
    useLayoutEffect(() => {
        const isReturning = Boolean(sessionStorage.getItem('cg_scroll_/blog'));
        if (isReturning) {
            restoreScrollPosition('/blog');
            return;
        }

        window.scrollTo(0, 0);
        if (window.__lenis) {
            window.__lenis.scrollTo(0, { immediate: true, force: true });
        }

        const ctx = gsap.context(() => {
            gsap.fromTo('.blog-reveal',
                { opacity: 0, y: 30 },
                { opacity: 1, y: 0, duration: 0.8, stagger: 0.05, ease: 'power3.out' }
            );
            gsap.fromTo('.blog-search-animate',
                { opacity: 0, y: 40 },
                { opacity: 1, y: 0, duration: 0.8, delay: 0.1, ease: 'power3.out' }
            );
        });
        return () => ctx.revert();
    }, []);

    const handleBlogClick = useCallback((blogOrId) => {
        const currentScroll = (window.__lenis ? window.__lenis.scroll : null) ?? window.scrollY ?? 0;
        if (currentScroll > 0) {
            sessionStorage.setItem('cg_scroll_/blog', String(currentScroll));
        }
        const blogObj = typeof blogOrId === 'object' ? blogOrId : (blogs || []).find(b => (b._id === blogOrId || b.id === blogOrId));
        const blogId = typeof blogOrId === 'object' ? (blogOrId._id || blogOrId.id) : blogOrId;
        if (blogObj) {
            dispatch(setCurrentBlog(blogObj));
        }
        if (blogId) {
            sessionStorage.setItem('cg_last_blog_id_/blog', String(blogId));
        }
        navigate(`/blog/${blogId}`);
    }, [navigate, blogs, dispatch]);

    // Auto-fill screen if initial articles don't fill the viewport and more pages exist
    useEffect(() => {
        if (loading || isFetchingMore) return;
        if (pagination && pagination.currentPage < pagination.totalPages) {
            const isContentShort = document.documentElement.scrollHeight <= window.innerHeight + 600;
            if (isContentShort) {
                const nextPage = pagination.currentPage + 1;
                setIsFetchingMore(true);
                dispatch(fetchBlogsThunk({
                    status: 'Published',
                    page: nextPage,
                    limit: 12,
                    search: searchQuery.trim() || undefined
                })).finally(() => {
                    setIsFetchingMore(false);
                });
            }
        }
    }, [blogs.length, pagination, loading, isFetchingMore, searchQuery, dispatch]);

    // Handle Infinite scroll for fetching nextPage (Trigger early before reaching footer)
    useEffect(() => {
        const handleScroll = () => {
            if (loading || isFetchingMore) return;
            const threshold = 900; // Trigger 900px before document end so user never has to enter footer
            const isNearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - threshold;
            if (isNearBottom && pagination && pagination.currentPage < pagination.totalPages) {
                const nextPage = pagination.currentPage + 1;
                setIsFetchingMore(true);
                dispatch(fetchBlogsThunk({
                    status: 'Published',
                    page: nextPage,
                    limit: 12,
                    search: searchQuery.trim() || undefined
                })).finally(() => {
                    setIsFetchingMore(false);
                });
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [pagination, loading, isFetchingMore, searchQuery, dispatch]);

    const formatDate = (dateStr, fallbackIdx = 0) => {
        if (!dateStr) {
            const fallbackDays = [16, 18, 20, 22, 24, 26, 28, 30, 2, 3, 3, 4];
            const fallbackMonths = ['Jun', 'Jun', 'Jun', 'Jun', 'Jun', 'Jun', 'Jun', 'Jun', 'Jul', 'Jul', 'Jul', 'Jul'];
            const day = fallbackDays[fallbackIdx % fallbackDays.length];
            const month = fallbackMonths[fallbackIdx % fallbackMonths.length];
            return `${day}, ${month} 2026`;
        }
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        const day = d.getDate();
        const month = d.toLocaleString('en-US', { month: 'short' });
        const year = d.getFullYear();
        return `${day}, ${month} ${year}`;
    };

    return (
        <div ref={containerRef} className="min-h-screen pt-24 pb-12 sm:pt-32 sm:pb-32 relative overflow-hidden text-gray-300 bg-transparent selection:bg-cyber-blue/30 font-cyber">
            <Helmet>
                <title>Gaming News & Articles | CUBE GAMES Blog</title>
                <meta name="description" content="Read the latest official game updates, esports highlights, game reviews, and walkthrough guides on the Cube Games Blog." />
                <meta name="keywords" content="gaming blog, esports news, game reviews, walkthrough guides, gaming articles" />
                <link rel="canonical" href="https://cubegames.ca/blog" />
                <meta property="og:type" content="website" />
                <meta property="og:title" content="Gaming News & Articles | CUBE GAMES Blog" />
                <meta property="og:description" content="Read the latest official game updates, esports highlights, game reviews, and walkthrough guides on the Cube Games Blog." />
                <meta property="og:url" content="https://cubegames.ca/blog" />
            </Helmet>

            {/* Ambient Lighting Glow FX */}
            <div
                className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[70vw] max-w-[900px] h-[400px] rounded-full blur-[140px] transition-all duration-700 pointer-events-none opacity-25"
                style={{
                    background: 'radial-gradient(circle, rgba(0,240,255,0.6) 0%, rgba(189,0,255,0.4) 50%, rgba(255,0,122,0.2) 80%, transparent 100%)'
                }}
            ></div>
            <div
                className="absolute top-[14%] left-1/2 -translate-x-1/2 w-[60vw] max-w-[750px] h-[350px] rounded-full blur-[120px] transition-all duration-700 pointer-events-none opacity-20 bg-[#00f0ff]"
            ></div>
            <div className="absolute top-1/3 left-[-10%] w-[500px] h-[500px] bg-[#00f0ff]/15 rounded-full blur-[160px] pointer-events-none"></div>
            <div className="absolute top-2/3 right-[-10%] w-[600px] h-[600px] bg-[#bd00ff]/15 rounded-full blur-[160px] pointer-events-none"></div>
            <div className="absolute bottom-[5%] left-1/2 -translate-x-1/2 w-[60vw] max-w-[800px] h-[350px] bg-gradient-to-t from-[#00f0ff]/10 via-[#bd00ff]/10 to-transparent rounded-full blur-[150px] pointer-events-none"></div>

            <div className="container mx-auto relative z-10 space-y-8 sm:space-y-10">

                {/* ─ Header, Search & Layout Switcher Section ─ */}
                <div ref={headerRef}>
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 sm:gap-6 pb-5 sm:pb-6">

                        {/* Title & Subtitle (matching Contact.jsx header style) */}
                        <div className="blog-reveal flex flex-col items-start text-left space-y-2 max-w-full min-w-0">
                            <p className="text-[10px] font-cyber font-semibold text-cyber-cyan uppercase tracking-[0.25em] flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse"></span>
                                GAMING HUB & CHRONICLES
                            </p>
                            <h1 className="font-cyber font-semibold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight">
                                Latest <span className="animate-gradient-text drop-shadow-[0_0_20px_rgba(0,240,255,0.35)]">Articles & News</span>
                            </h1>
                            <div className="h-[2px] w-20 animate-gradient-bg mt-3 rounded-full"></div>
                        </div>

                        {/* Right Tools: View Toggle & Search Input Bar */}
                        <div className="blog-search-animate flex flex-col sm:flex-row items-stretch sm:items-center justify-between sm:justify-end gap-3 w-full lg:w-auto shrink-0">

                            {/* View Switcher Controls (Vertical Grid vs Horizontal Row) */}
                            <div className="hidden md600:flex items-center justify-center sm:justify-start bg-[#0d101d]/90 border border-white/15 rounded-lg p-1 backdrop-blur-md shrink-0">
                                <button
                                    onClick={() => setViewMode('vertical')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] sm:text-xs  transition-all ${viewMode === 'vertical'
                                        ? 'bg-white text-black shadow-md shadow-white/10'
                                        : 'text-gray-400 hover:text-white'
                                        }`}
                                    title="Vertical Grid View"
                                >
                                    <Grid className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => setViewMode('horizontal')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] sm:text-xs  transition-all ${viewMode === 'horizontal'
                                        ? 'bg-white text-black shadow-md shadow-white/10'
                                        : 'text-gray-400 hover:text-white'
                                        }`}
                                    title="Horizontal Row View"
                                >
                                    <List className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Search Input Bar */}
                            <div className="relative w-full sm:w-60 md:w-64 lg:w-72">
                                <input
                                    type="text"
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    placeholder="Search topics here..."
                                    className="w-full bg-[#0d101d]/90 border border-white/15 rounded-lg pl-9 pr-8 py-2 text-xs sm:text-sm text-white placeholder-gray-400 focus:outline-none focus:border-[#00f0ff]/70 transition-all shadow-inner backdrop-blur-md "
                                />
                                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                {searchInput && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchInput('');
                                            setSearchQuery('');
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white text-xs font-bold"
                                        aria-label="Clear search"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                        </div>
                    </div>
                </div>

                {!isInitialFetched || (loading && blogs.length === 0) ? (
                    <div
                        ref={setGridRef}
                        className={
                            viewMode === 'vertical'
                                ? "grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 items-stretch"
                                : "grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-stretch"
                        }
                    >
                        {[...Array(8)].map((_, n) => (
                            <BlogCardSkeleton key={n} viewMode={viewMode} style={getCornerStyle(n, 8, gridCols)} />
                        ))}
                    </div>
                ) : blogs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-32 text-center">
                        <Search className="w-12 h-12 text-gray-700 mb-4 animate-pulse" />
                        <h3 className="text-xl font-cyber font-semibold text-white uppercase mb-2">No articles found</h3>
                        <p className="text-gray-500 font-cyber text-xs uppercase tracking-widest">Try adjusting your search or topic.</p>
                    </div>
                ) : (
                    /* ─ Responsive Card Layout Container (Vertical Grid vs Horizontal Row View) ─ */
                    <div
                        ref={setGridRef}
                        className={
                            viewMode === 'vertical'
                                ? "grid grid-cols-1 sm500:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6 items-stretch"
                                : "grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 items-stretch"
                        }
                    >
                        {blogs.map((blog, idx) => {
                            const blogId = blog._id || blog.id || idx;
                            const readTime = Math.max(2, Math.ceil((blog.content?.length || 1000) / 300));
                            const categoryLabel = blog.category || 'GAMES';

                            /* ─ 1. VERTICAL CARD VIEW DESIGN ─ */
                            if (viewMode === 'vertical') {
                                return (
                                    <article
                                        key={blogId}
                                        id={`blog-card-${blogId}`}
                                        data-blog-id={blogId}
                                        data-blog-index={idx}
                                        onClick={() => handleBlogClick(blog)}
                                        style={getCornerStyle(idx, blogs.length, gridCols)}
                                        className="group relative bg-[#080814]/95 backdrop-blur-xl group-hover:border-cyber-cyan/50 cyber-card-glow overflow-hidden shadow-xl transition-all duration-300 cursor-pointer flex flex-col justify-between min-w-0 animate-in fade-in duration-500 h-full"
                                    >
                                        <div>
                                            {/* Card Image Banner */}
                                            <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#05060f] shrink-0" style={{ borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
                                                <ProgressiveImage
                                                    src={blog.image?.url || blog.image}
                                                    placeholderSrc={blog.image?.lowUrl || blog.imageLow || blog.image?.url || blog.image}
                                                    alt={blog.title}
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-[#080814] via-transparent to-transparent opacity-75 pointer-events-none" />

                                                {/* Top-Left Category Badge */}
                                                {/* <div className="absolute top-2.5 left-2.5 z-10">
                                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/65 backdrop-blur-md border border-white/15 text-[10px] font-cyber font-bold tracking-wider text-cyber-cyan uppercase shadow-md group-hover:border-cyber-cyan/50 group-hover:shadow-[0_0_10px_rgba(0,240,255,0.3)] transition-all">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse shrink-0"></span>
                                                        <span className="truncate max-w-[120px]">{categoryLabel}</span>
                                                    </span>
                                                </div> */}

                                                {/* Read Time Overlay Pill */}
                                                <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-md border border-white/10 text-[10px] font-mono text-gray-300 font-semibold shadow-md">
                                                    <Clock className="w-3 h-3 text-purple-400" />
                                                    <span>{readTime}m read</span>
                                                </div>
                                            </div>

                                            {/* Card Details Container */}
                                            <div className="p-4 sm:p-5 space-y-2.5 min-w-0">
                                                {/* Meta Info Bar */}
                                                <div className="flex items-center justify-between text-[11px] text-gray-400 font-mono">
                                                    <span className="flex items-center gap-1.5 text-gray-400">
                                                        <Calendar className="w-3.5 h-3.5 text-[#00f0ff]" />
                                                        {formatDate(blog.createdAt, idx)}
                                                    </span>
                                                </div>

                                                {/* Article Title: Line Clamp 1 */}
                                                <h2 className="text-sm sm:text-base font-cyber font-bold tracking-tight text-white line-clamp-1 truncate uppercase" title={blog.title}>
                                                    {blog.title}
                                                </h2>

                                                {/* Excerpt Body: Line Clamp 1 */}
                                                <p className="text-[11px] sm:text-xs text-gray-400 font-sans leading-relaxed line-clamp-1 truncate">
                                                    {blog.excerpt || (blog.content ? blog.content.replace(/<[^>]*>?/gm, '').slice(0, 120) : 'Stay updated with complete game reviews, patch updates, and esports coverage.')}
                                                </p>
                                            </div>
                                        </div>

                                        {/* Bottom Action Footer */}
                                        <div className="px-4 sm:px-5 pb-4 sm:pb-5 pt-3 border-t border-white/5 flex items-center justify-between text-xs text-[#00f0ff] font-cyber font-semibold tracking-wider">
                                            <span className="group-hover:translate-x-1.5 transition-transform inline-flex items-center gap-1.5">
                                                READ ARTICLE <ArrowRight className="w-3.5 h-3.5" />
                                            </span>
                                        </div>
                                    </article>
                                );
                            }

                            /* ─ 2. HORIZONTAL CARD VIEW DESIGN (2-COLUMN GRID) ─ */
                            return (
                                <article
                                    key={blogId}
                                    id={`blog-card-${blogId}`}
                                    data-blog-id={blogId}
                                    data-blog-index={idx}
                                    onClick={() => handleBlogClick(blog)}
                                    style={getCornerStyle(idx, blogs.length, gridCols)}
                                    className="group relative bg-[#080814]/95 backdrop-blur-xl group-hover:border-cyber-cyan/50 cyber-card-glow overflow-hidden shadow-xl hover:shadow-[0_0_25px_rgba(0,240,255,0.18)] transition-all duration-300 cursor-pointer flex flex-col md600:flex-row items-stretch min-w-0 animate-in fade-in duration-500 h-full"
                                >
                                    {/* Horizontal Left Thumbnail Banner */}
                                    <div className="relative w-full md600:w-48 lg:w-56 aspect-[16/10] overflow-hidden bg-[#05060f] shrink-0">
                                        <ProgressiveImage
                                            src={blog.image?.url || blog.image}
                                            placeholderSrc={blog.image?.lowUrl || blog.imageLow || blog.image?.url || blog.image}
                                            alt={blog.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#080814]/80 via-transparent to-transparent opacity-60 pointer-events-none md600:hidden" />

                                        {/* Top-Left Category Badge */}
                                        {/* <div className="absolute top-2.5 left-2.5 z-10">
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/65 backdrop-blur-md border border-white/15 text-[10px] font-cyber font-bold tracking-wider text-cyber-cyan uppercase shadow-md group-hover:border-cyber-cyan/50 group-hover:shadow-[0_0_10px_rgba(0,240,255,0.3)] transition-all">
                                                <span className="w-1.5 h-1.5 rounded-full bg-cyber-cyan shadow-[0_0_6px_#00f0ff] animate-pulse shrink-0"></span>
                                                <span className="truncate max-w-[100px]">{categoryLabel}</span>
                                            </span>
                                        </div> */}

                                        {/* Read Time Overlay (Mobile) */}
                                        <div className="absolute bottom-2.5 right-2.5 md600:hidden flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-md border border-white/10 text-[10px] font-mono text-gray-300 font-semibold shadow-md">
                                            <Clock className="w-3 h-3 text-purple-400" />
                                            <span>{readTime}m read</span>
                                        </div>
                                    </div>

                                    {/* Horizontal Right Details Content */}
                                    <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between space-y-2.5 min-w-0">
                                        <div className="space-y-2 min-w-0">
                                            {/* Meta Bar */}
                                            <div className="flex items-center justify-between text-[11px] text-gray-400 font-mono">
                                                <span className="flex items-center gap-1.5 text-gray-400">
                                                    <Calendar className="w-3.5 h-3.5 text-[#00f0ff]" />
                                                    {formatDate(blog.createdAt, idx)}
                                                </span>
                                                <span className="hidden md600:flex items-center gap-1.5 text-gray-400">
                                                    <Clock className="w-3.5 h-3.5 text-purple-400" />
                                                    {readTime} min read
                                                </span>
                                            </div>

                                            {/* Title */}
                                            <h2 className="text-xs sm:text-sm md:text-base font-cyber font-bold tracking-tight text-white group-hover:text-[#00f0ff] transition-colors leading-snug line-clamp-1 truncate uppercase" title={blog.title}>
                                                {blog.title}
                                            </h2>

                                            {/* Excerpt: Line Clamp 1 */}
                                            <p className="text-[11px] sm:text-xs text-gray-400 font-sans leading-relaxed line-clamp-1 truncate">
                                                {blog.excerpt || (blog.content ? blog.content.replace(/<[^>]*>?/gm, '').slice(0, 140) : 'Stay updated with complete game reviews, patch updates, and esports coverage.')}
                                            </p>
                                        </div>

                                        {/* Action link */}
                                        <div className="pt-2.5 border-t border-white/5 flex items-center justify-between text-xs text-[#00f0ff] font-cyber font-semibold tracking-wider">
                                            <span className="group-hover:translate-x-1.5 transition-transform inline-flex items-center gap-1.5">
                                                READ ARTICLE <ArrowRight className="w-3.5 h-3.5" />
                                            </span>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}

                {/* Scroll loading indicator (Only show while actively fetching next page via scroll) */}
                {isFetchingMore && (
                    <div className="flex justify-center mt-12 mb-6">
                        <div className="flex items-center gap-2 px-6 py-3 rounded-full bg-white/[0.02] border border-white/5 text-[#00f0ff] font-cyber animate-pulse">
                            <Loader2 className="w-5 h-5 animate-spin text-[#00f0ff]" />
                            <span>LOADING MORE ARTICLES...</span>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}
